import React from 'react';

export const faqItems = [
  {
    number: '01',
    eyebrow: 'Workflow',
    question: 'What does SubtitleTsunami do?',
    answer:
      'SubtitleTsunami helps you upload videos, generate English to Spanish subtitles with AI, and download the finished subtitled video.',
  },
  {
    number: '02',
    eyebrow: 'Output',
    question: 'What do I get after processing?',
    answer:
      'After processing, SubtitleTsunami gives you a downloadable video with Spanish subtitles added to the file.',
  },
  {
    number: '03',
    eyebrow: 'Language',
    question: 'Is SubtitleTsunami for English to Spanish subtitles?',
    answer:
      'Yes. SubtitleTsunami focuses on generating Spanish subtitles from English video content.',
  },
  {
    number: '04',
    eyebrow: 'Processing',
    question: 'How long does video processing take?',
    answer:
      'Processing time depends on the video, but generating subtitles and preparing your download may take up to a few minutes.',
  },
  {
    number: '05',
    eyebrow: 'Pricing',
    question: 'Does SubtitleTsunami have a free option?',
    answer:
      'Yes. SubtitleTsunami includes a free daily upload option and a Starter plan for users who need more videos per day.',
  },
];

const HomeFAQSection = () => {
  return (
    <section
      id="faq"
      className="relative mt-10 overflow-hidden rounded-[2rem] border border-cyan-300/15 bg-slate-950/60 p-5 shadow-2xl shadow-cyan-950/30 backdrop-blur-xl sm:p-6 lg:p-8"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(34,211,238,0.18),transparent_30%),radial-gradient(circle_at_85%_30%,rgba(59,130,246,0.16),transparent_32%),linear-gradient(135deg,rgba(15,23,42,0.3),rgba(8,47,73,0.2))]" />

      <div className="pointer-events-none absolute -left-24 top-10 h-48 w-48 rounded-full bg-cyan-300/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-12 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/50 to-transparent" />

      <div className="relative">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mx-auto inline-flex items-center gap-3 rounded-full border border-cyan-300/20 bg-cyan-400/10 px-4 py-2 shadow-lg shadow-cyan-950/20">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-300 opacity-60" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-cyan-200" />
            </span>

            <p className="text-xs font-black uppercase tracking-[0.28em] text-cyan-200">
              FAQ
            </p>
          </div>

          <h2 className="mt-5 text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
            English to Spanish subtitles,
            <span className="block bg-gradient-to-r from-cyan-200 via-sky-200 to-blue-300 bg-clip-text text-transparent">
              without the messy workflow
            </span>
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
            Quick answers for creators who want translated captions and a clean
            download flow without bouncing between five different tools.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {faqItems.map((item, index) => {
            const isFeatureCard = index === 0;

            return (
              <article
                key={item.question}
                className={[
                  'group relative overflow-hidden rounded-3xl border p-5 transition duration-300 hover:-translate-y-1 hover:border-cyan-200/40 hover:bg-white/[0.075] hover:shadow-2xl hover:shadow-cyan-950/40 sm:p-6',
                  isFeatureCard
                    ? 'border-cyan-200/25 bg-cyan-300/[0.07] lg:col-span-2'
                    : 'border-white/10 bg-white/[0.045]',
                ].join(' ')}
              >
                <span className="absolute inset-0 translate-x-[-125%] bg-gradient-to-r from-transparent via-cyan-200/10 to-transparent transition duration-1000 group-hover:translate-x-[125%]" />

                <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-cyan-300/10 blur-2xl transition duration-500 group-hover:bg-cyan-300/20" />

                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 opacity-50">
                  <div className="absolute bottom-[-38px] left-[-10%] h-24 w-[120%] rounded-[50%] border-t border-cyan-200/15 bg-cyan-400/[0.035] transition duration-500 group-hover:bottom-[-32px] group-hover:bg-cyan-400/[0.06]" />
                  <div className="absolute bottom-[-52px] left-[-20%] h-28 w-[140%] rounded-[50%] border-t border-blue-200/10 bg-blue-500/[0.025] transition duration-700 group-hover:bottom-[-44px]" />
                </div>

                <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start">
                  <div className="flex items-center gap-3 sm:block">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-cyan-200/20 bg-slate-950/70 text-lg font-black text-cyan-100 shadow-lg shadow-cyan-950/25">
                      {item.number}
                    </div>

                    <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300 sm:mt-3">
                      {item.eyebrow}
                    </p>
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="text-xl font-black leading-snug text-white sm:text-2xl">
                      {item.question}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-300 sm:text-base">
                      {item.answer}
                    </p>

                    {isFeatureCard && (
                      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl border border-cyan-300/15 bg-slate-950/55 px-4 py-3">
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                            Upload
                          </p>
                          <p className="mt-1 text-xs leading-5 text-slate-400">
                            Start with your video file.
                          </p>
                        </div>

                        <div className="rounded-2xl border border-cyan-300/15 bg-slate-950/55 px-4 py-3">
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                            Generate
                          </p>
                          <p className="mt-1 text-xs leading-5 text-slate-400">
                            Create Spanish subtitles with AI.
                          </p>
                        </div>

                        <div className="rounded-2xl border border-cyan-300/15 bg-slate-950/55 px-4 py-3">
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                            Download
                          </p>
                          <p className="mt-1 text-xs leading-5 text-slate-400">
                            Save the finished video.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-8 overflow-hidden rounded-3xl border border-emerald-300/15 bg-emerald-400/[0.06] p-5 shadow-xl shadow-emerald-950/20">
          <div className="group relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="absolute inset-0 translate-x-[-125%] bg-gradient-to-r from-transparent via-emerald-200/10 to-transparent transition duration-1000 group-hover:translate-x-[125%]" />

            <div className="relative">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-emerald-300">
                Built for speed
              </p>

              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                One free daily upload lets users test the workflow before
                upgrading to the Starter plan for heavier subtitle work.
              </p>
            </div>

            <div className="relative inline-flex rounded-2xl border border-emerald-300/20 bg-slate-950/60 px-4 py-3 text-sm font-black text-emerald-100 shadow-lg shadow-emerald-950/20">
              1 free/day · Starter available
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeFAQSection;